import { VMHAConditionKey, type VMHAStatusDetails } from 'api-client/model'
import { CLUSTER_HOST_SCHEDULING } from 'app/constants'
import { ephemeralSharedStorageDoc } from 'app/docLinks'
import ExternalLink from 'core/components/ExternalLink'
import type { BadgeVariant } from 'core/elements/badge/Badge'
import { DEFAULT_NODE_GROUP_STATUS, type NodeGroupStatus } from 'k8s/components/dashboard/helpers'
import { isVolumeBackedVm } from '../../vms/helpers'
import { errorStatuses, healthyStatuses, pendingStatuses } from '../selectors'
import {
  type Cpu,
  type IClusterHostGroup,
  Status,
  type VMHACondition,
  VMHADisplayStatus,
} from './model'

// Fields added to each host by resmgrHostsSelector, the same ones the
// hosts table shows in its Connection Status and Role Status columns
interface HostWithStatus {
  responding?: string
  roleStatuses?: Array<{ status?: string }>
}

export const generateHostStatus = (
  hosts: HostWithStatus[] | null | undefined,
): Array<NodeGroupStatus & { tooltip: string }> => {
  const hostStatus = {
    success: { ...DEFAULT_NODE_GROUP_STATUS.success, tooltip: 'Online, all roles applied' },
    error: { ...DEFAULT_NODE_GROUP_STATUS.error, tooltip: 'Offline or role failed' },
    warning: { ...DEFAULT_NODE_GROUP_STATUS.warning, tooltip: 'Role being applied or removed' },
  }

  hosts?.forEach((host) => {
    const statuses = host?.roleStatuses?.map((role) => role?.status) || []
    // FR-CHDP-005: an offline host or a failed role is a problem
    if (host?.responding === 'offline' || statuses.some((s) => errorStatuses.includes(s))) {
      hostStatus.error.label++
    } else if (statuses.every((s) => healthyStatuses.includes(s))) {
      // FR-CHDP-006: all roles applied (or no roles) is healthy
      hostStatus.success.label++
    } else {
      // FR-CHDP-006: a role is being applied or removed, or has a status we don't know
      hostStatus.warning.label++
    }
  })

  return Object.values(hostStatus)
}

export const getClusterHostGroupBadgeVariant = (status: string): BadgeVariant => {
  switch (status) {
    case Status.Healthy:
      return 'success'
    case Status.Unhealthy:
      return 'error'
    case Status.Maintenance:
      return 'warning'
    case Status.Error:
      return 'error'
    case Status.Unknown:
    default:
      return 'unknown'
  }
}

interface HostUsage {
  usage?: {
    computeAllocation?: { current?: number; max?: number }
    memory?: { current?: number; max?: number }
    ephemeralDisk?: { current?: number; max?: number }
  }
}

export const getAggregatedUsage = (
  hostList: HostUsage[] | null | undefined,
  ephemeralStorageShared = false,
) => {
  const totalUsage = {
    cpuUsage: { current: 0, total: 0 },
    memoryUsage: { current: 0, total: 0 },
    storageUsage: { current: 0, total: 0 },
  }

  // biome-ignore lint/complexity/noForEach: I am happy with forEach here
  hostList?.forEach((host) => {
    totalUsage.cpuUsage.current += host?.usage?.computeAllocation?.current || 0
    totalUsage.cpuUsage.total += host?.usage?.computeAllocation?.max || 0
    totalUsage.memoryUsage.current += host?.usage?.memory?.current || 0
    totalUsage.memoryUsage.total += host?.usage?.memory?.max || 0
    // Same ephemeral source as the Hosts list Ephemeral Storage column.
    totalUsage.storageUsage.current += host?.usage?.ephemeralDisk?.current || 0
    totalUsage.storageUsage.total += host?.usage?.ephemeralDisk?.max || 0
  })

  // A shared instance storage pool is one disk, so count it once per cluster.
  if (ephemeralStorageShared) {
    const pool = hostList?.map((host) => host?.usage?.ephemeralDisk).find((d) => d?.max > 0)
    totalUsage.storageUsage = { current: pool?.current || 0, total: pool?.max || 0 }
  }

  // Round off the values to 2 decimal places
  // biome-ignore lint/complexity/noForEach: I am happy with forEach here
  Object.keys(totalUsage).forEach((usageType) => {
    totalUsage[usageType].current = parseFloat(totalUsage[usageType].current.toFixed(2))
    totalUsage[usageType].total = parseFloat(totalUsage[usageType].total.toFixed(2))
  })

  return totalUsage
}

export const getVMHAConditions = (includeHASlaveStatus: boolean = true) =>
  [
    {
      key: VMHAConditionKey.TwoActiveHosts,
      label: 'At least two active hosts exist in the cluster.',
    },
    {
      key: VMHAConditionKey.SharedStorage,
      label: (
        <>
          All VMs across cluster hosts either use a block storage volume as the root disk or{' '}
          <ExternalLink url={ephemeralSharedStorageDoc}>Ephemeral Shared Storage</ExternalLink> for
          VMs using ephemeral storage for the root disk.
        </>
      ),
    },
    {
      key: VMHAConditionKey.ImageLibrary,
      label: 'At least two hosts have the image library role assigned.',
    },
    {
      key: VMHAConditionKey.PersistentStorage,
      label: 'At least two hosts have the persistent storage role assigned.',
    },
    ...(includeHASlaveStatus
      ? [
          {
            key: VMHAConditionKey.HaSlaveStatus,
            label: 'The VM HA role is successfully applied to all hypervisor hosts in the cluster.',
          },
        ]
      : []),
  ] as const

export const mapVMHAConditions = (
  vmhaClusters: VMHAStatusDetails,
  includeHASlaveStatus: boolean = true,
): VMHACondition[] => {
  const conditions = vmhaClusters?.conditions ?? {}

  return getVMHAConditions(includeHASlaveStatus)?.map(({ key, label }) => ({
    key,
    label,
    passed: conditions[key] === 'pass',
    degraded: conditions[key] === 'degraded',
  }))
}

const VMWARE_ROLES = ['pf9-ostackhost-neutron-vmw', 'pf9-glance-role-vmw']

interface EligibilityHost {
  hostconfig_id?: string | null
  gpuModels?: string[]
  responding?: string
  schedulingStatus?: string
  v1Roles?: string[]
  vmwareRolesIncomplete?: boolean
  roleStatuses?: Array<{ status: string }>
}

interface EligibilityCluster {
  gpu?: { enabled?: boolean }
}

interface VmForVmhaStatus {
  clusterName?: string
  volumeDetails?: Record<string, { boot_index?: number }>
}

interface BlueprintForVmhaStatus {
  instanceSharedStorage?: boolean
}

// FR-VSC-005: Per-VM HA status inherits the VM's cluster's computed vmhaStatus as-is,
// downgrading to Not Protected only when the cluster is otherwise Protected but this
// VM's own storage (neither shared nor volume-backed) would lose data on host outage.
// FR-VSC-007: When the cluster's VMHA setting excludes ephemeral VMs, a VM that isn't
// volume-backed is never evacuated regardless of the cluster's own Protected/Degraded/
// Not Protected status, so it reports Excluded instead — unless the cluster has VMHA
// disabled entirely or its status hasn't loaded yet, which take priority. This check is
// independent of the blueprint's shared-storage flag: the cluster-level setting excludes
// ephemeral (non-volume-backed) VMs outright, regardless of that DU-wide default.
export const deriveVmVmhaStatus = (
  clusterHostGroups: IClusterHostGroup[] | undefined,
  item: VmForVmhaStatus | undefined,
  blueprint: BlueprintForVmhaStatus | undefined,
): VMHADisplayStatus | null => {
  if (!clusterHostGroups?.length || !item || !blueprint) {
    return null
  }

  const cluster = clusterHostGroups?.find((c) => c?.name === item?.clusterName)
  const clusterStatus = cluster?.vmhaStatus ?? VMHADisplayStatus.DISABLED
  const isVolumeBacked = isVolumeBackedVm(item)
  const isSafeStorage = blueprint?.instanceSharedStorage || isVolumeBacked

  if (clusterStatus === VMHADisplayStatus.DISABLED || clusterStatus === VMHADisplayStatus.LOADING) {
    return clusterStatus
  }

  if (cluster?.vmhaState?.exclude_ephemeral_vms && !isVolumeBacked) {
    return VMHADisplayStatus.EXCLUDED
  }

  return clusterStatus === VMHADisplayStatus.PROTECTED && !isSafeStorage
    ? VMHADisplayStatus.NOT_PROTECTED
    : clusterStatus
}

export const getHostEligibilityError = (
  host: EligibilityHost,
  cluster: EligibilityCluster,
): string | null => {
  const isGpuHost = (host.gpuModels?.length ?? 0) > 0
  const isGpuCluster = cluster?.gpu?.enabled === true
  if (!isGpuHost && isGpuCluster) {
    return "Host GPU capability does not match the cluster's GPU configuration."
  }

  if (host.responding === 'offline') {
    return 'Host is offline. Resolve connectivity before adding to a cluster.'
  }

  if (host.schedulingStatus === CLUSTER_HOST_SCHEDULING.disabled) {
    return 'Host is in maintenance mode. Disable maintenance mode before adding to a cluster.'
  }

  const hasVmwareRole = host.v1Roles?.some((r: string) => VMWARE_ROLES.includes(r))
  if (hasVmwareRole || host.vmwareRolesIncomplete) {
    return 'VMware hosts cannot be added to a cluster using this flow.'
  }

  const hasRoleConverging = host.roleStatuses?.some((rs) => pendingStatuses.includes(rs.status))
  if (hasRoleConverging) {
    return 'A role operation is already in progress on this host. Wait for it to complete before adding to a cluster.'
  }

  const hasRoleError = host.roleStatuses?.some((rs) => errorStatuses.includes(rs.status))
  if (hasRoleError) {
    return 'This host has a role error. Resolve the error before adding to a cluster.'
  }

  return null
}

// Shared by CreateClusterHostGroupsModal and EditClusterHostGroupModal so the
// CPU Mode picklist value maps to the same ResMgr body shape in both flows.
export const mapCpuModeToBody = (cpuMode: string, cpuModel: string): Cpu => ({
  mode: cpuMode === 'default' ? null : cpuMode,
  model: cpuMode === 'custom' ? cpuModel : cpuMode === 'default' ? null : '',
})
